import {
  AgreementLang,
  AgreementText,
  StaffAgreementData,
  agreementTitle,
} from '@app/staff/agreement/agreement-text';
import {useStudentView} from '@app/staff/student-view';
import {apiClient, queryClient} from '@common/http/query-client';
import {useMutation, useQuery} from '@tanstack/react-query';
import {Button} from '@ui/buttons/button';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';
import axios from 'axios';
import {useEffect, useState} from 'react';
import {useLocation} from 'react-router';

interface Response {
  agreement: StaffAgreementData | null;
  required: boolean;
}

const QUERY_KEY = ['staff-agreement'];
const STAFF_AREAS = /^\/(dashboard|admin|staff|agent)(\/|$)/;

/** Words on the popup itself, in the language the agreement is shown in. */
const UI: Record<AgreementLang, Record<string, string>> = {
  en: {
    confirm: 'I have read the agreement and commit to all of its terms',
    agree: 'I agree and enter the system',
    version: 'Version',
    other: 'العربية',
    note: 'Your acceptance is recorded with the date, time, IP address and device.',
  },
  ar: {
    confirm: 'قرأت الاتفاقية وأقر بالالتزام بجميع بنودها',
    agree: 'أوافق وأدخل إلى النظام',
    version: 'النسخة',
    other: 'English',
    note: 'تُسجَّل موافقتك مع التاريخ والوقت وعنوان الـIP والجهاز.',
  },
};

/**
 * Staff must accept the current work agreement before using the system.
 *
 * Shown over every staff page (dashboard, admin, staff portal) and cannot be
 * closed: the only way on is to tick the box and press "I agree". The server
 * enforces the same thing — staff APIs answer 428 until it is accepted — so
 * this listens for that answer too (e.g. a new version published mid-shift).
 */
export function StaffAgreementGate() {
  const {is_staff, impersonating} = useStudentView();
  const {pathname} = useLocation();
  const enabled = is_staff && !impersonating;

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get<Response>('staff-agreement').then(r => r.data),
    enabled,
    staleTime: 5 * 60_000,
  });

  // A staff API refusing with 428 means the agreement (re)became required.
  useEffect(() => {
    if (!enabled) return;
    const id = apiClient.interceptors.response.use(undefined, error => {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 428 &&
        (error.response.data as any)?.code === 'staff_agreement_required'
      ) {
        queryClient.invalidateQueries({queryKey: QUERY_KEY});
      }
      return Promise.reject(error);
    });
    return () => apiClient.interceptors.response.eject(id);
  }, [enabled]);

  if (
    !enabled ||
    !query.data?.required ||
    !query.data.agreement ||
    !STAFF_AREAS.test(pathname)
  ) {
    return null;
  }
  return <AgreementPopup agreement={query.data.agreement} />;
}

function AgreementPopup({agreement}: {agreement: StaffAgreementData}) {
  const {localeCode} = useSelectedLocale();
  const [lang, setLang] = useState<AgreementLang>(
    localeCode === 'en' ? 'en' : 'ar',
  );
  const [checked, setChecked] = useState(false);
  const t = UI[lang];

  const accept = useMutation({
    mutationFn: () =>
      apiClient.post('staff-agreement/accept', {
        agreement_id: agreement.id,
        language: lang,
        confirmed: checked,
      }),
    onSuccess: () => {
      queryClient.setQueryData(QUERY_KEY, (old: Response | undefined) =>
        old ? {...old, required: false} : old,
      );
      // Anything that failed with 428 while the popup was up can load now.
      queryClient.invalidateQueries();
    },
    onError: err => {
      const msg =
        (axios.isAxiosError(err) &&
          ((err.response?.data as any)?.errors?.agreement_id?.[0] ||
            (err.response?.data as any)?.message)) ||
        null;
      toast.danger(msg || message('Something went wrong. Please try again.'));
      queryClient.invalidateQueries({queryKey: QUERY_KEY});
    },
  });

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-12 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="staff-agreement-title"
        dir={lang === 'ar' ? 'rtl' : 'ltr'}
        className="flex max-h-[92vh] w-full max-w-720 flex-col overflow-hidden rounded-panel bg-paper shadow-2xl"
      >
        <div className="flex items-start justify-between gap-12 border-b px-24 py-16">
          <div>
            <h2 id="staff-agreement-title" className="text-lg font-bold">
              {agreementTitle(agreement, lang)}
            </h2>
            <div className="mt-2 text-xs text-muted">
              {t.version} {agreement.version}
            </div>
          </div>
          <Button
            size="xs"
            variant="outline"
            onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
          >
            {t.other}
          </Button>
        </div>
        <div className="flex-auto overflow-y-auto px-24 py-16">
          <AgreementText agreement={agreement} lang={lang} />
        </div>
        <div className="border-t bg-alt px-24 py-16">
          <label className="flex cursor-pointer items-start gap-10 text-sm font-medium">
            <input
              type="checkbox"
              className="mt-2 h-18 w-18 flex-shrink-0 accent-primary"
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
            />
            <span>{t.confirm}</span>
          </label>
          <div className="mt-12 flex flex-wrap items-center justify-between gap-12">
            <p className="text-xs text-muted">{t.note}</p>
            <Button
              variant="flat"
              color="primary"
              disabled={!checked || accept.isPending}
              onClick={() => accept.mutate()}
            >
              {t.agree}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
